import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, QUESTION_TAGS } from '../utils/constants';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSend, FiLoader, FiCheckCircle } = FiIcons;

const QuestionForm = ({ onBack, preselectedCategory = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: preselectedCategory || '',
    description: '',
    tags: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.tags.length === 0) {
      setError('Please select at least one tag.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in to submit a question.');

      const { data, error: submitError } = await supabase
        .from('questions_kb2024')
        .insert([
          {
            title: formData.title,
            description: formData.description,
            category: formData.category,
            tags: formData.tags,
            asked_by: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            status: 'Open'
          }
        ])
        .select();

      if (submitError) {
        console.error('Supabase error:', submitError);
        throw submitError;
      }

      console.log('Question submitted successfully:', data);

      // Show success state
      setSuccess(true);

      // Reset form
      setFormData({
        title: '',
        category: preselectedCategory || '',
        description: '',
        tags: []
      });

      // Wait a bit to show success message, then navigate back
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (error) {
      console.error('Error submitting question:', error);
      setError(`Failed to submit question: ${error.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTagChange = (tag) => {
    const updatedTags = formData.tags.includes(tag)
      ? formData.tags.filter(t => t !== tag)
      : [...formData.tags, tag];
    setFormData({
      ...formData,
      tags: updatedTags
    });
  };

  if (success) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Question Submitted!</h2>
          <p className="text-gray-600 mb-4">Your question has been successfully submitted and will appear in the dashboard.</p>
          <div className="flex items-center justify-center gap-2 text-green-600">
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
            <span>Redirecting to dashboard...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
          disabled={submitting}
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Ask a Question</h1>
        <p className="text-gray-600">Share your question with the community and get expert answers</p>
        {preselectedCategory && (
          <p className="text-sm text-blue-600 mt-2">Category: {preselectedCategory}</p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8"
      >
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Question Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              disabled={submitting}
              placeholder="Enter a clear and concise question title"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              disabled={!!preselectedCategory || submitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags * (Select at least one)
            </label>
            <div className="flex flex-wrap gap-2">
              {QUESTION_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagChange(tag)}
                  disabled={submitting}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 disabled:opacity-50 ${
                    formData.tags.includes(tag)
                      ? 'bg-blue-100 border-blue-300 text-blue-800'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            {formData.tags.length === 0 && (
              <p className="mt-2 text-sm text-red-600">Please select at least one tag</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Question Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              disabled={submitting}
              rows={6}
              placeholder="Provide detailed information about your question. Include context, what you've tried, and what specific help you need."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none disabled:bg-gray-100"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || formData.tags.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                  Submit Question
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default QuestionForm;